import React, { useState, useEffect, useRef } from "react";
import styles from "./index.module.scss";

interface CircularTimerProps {
  isVisible: boolean;
  duration?: number;
  onComplete: () => void;
}

const CircularTimer: React.FC<CircularTimerProps> = ({
  isVisible,
  duration = 60,
  onComplete,
}) => {
  const [timeLeft, setTimeLeft] = useState(duration);
  const [progress, setProgress] = useState(0);
  const cancelledRef = useRef(false);
  const animationFrameRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isVisible) {
      cancelledRef.current = true;
      setTimeLeft(duration);
      setProgress(0);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      return;
    }
    cancelledRef.current = false;
    setTimeLeft(duration);
    setProgress(0);

    const startTime = Date.now();
    const endTime = startTime + duration * 1000;

    const update = () => {
      if (cancelledRef.current) return;
      const now = Date.now();
      const remaining = Math.max(0, endTime - now);
      const elapsed = duration * 1000 - remaining;
      const progressValue = elapsed / (duration * 1000);
      setProgress(progressValue);

      const secondsLeft = Math.ceil(remaining / 1000);
      setTimeLeft(secondsLeft);

      if (remaining > 0) {
        animationFrameRef.current = requestAnimationFrame(update);
      } else {
        if (!cancelledRef.current) {
          onComplete();
        }
      }
    };

    animationFrameRef.current = requestAnimationFrame(update);

    return () => {
      cancelledRef.current = true;
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isVisible, duration, onComplete]);

  if (!isVisible) return null;

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const circumference = 2 * Math.PI * 64.5;
  const strokeDashoffset = circumference - progress * circumference;
  const dashArray = `${progress * circumference} ${circumference}`;

  return (
    <div className={styles.timer_container}>
      <div className={styles.timer_circle_container}>
        <svg width="132" height="132" className={styles.timer_svg}>
          <defs>
            <linearGradient
              id="timerGradient"
              x1="66"
              y1="0"
              x2="66"
              y2="132"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0%" stopColor="#3068BC" />
              <stop offset="100%" stopColor="#6EA9E1" />
            </linearGradient>
          </defs>
          <circle
            cx="66"
            cy="66"
            r="64.5"
            stroke="#e5e7eb"
            strokeWidth="3"
            fill="none"
          />
          <circle
            cx="66"
            cy="66"
            r="64.5"
            stroke="url(#timerGradient)"
            strokeWidth="3"
            fill="none"
            strokeLinecap="round"
            strokeDasharray={dashArray}
            strokeDashoffset="0"
            className={styles.timer_progress}
          />
        </svg>
        <div className={styles.timer_text}>{formatTime(timeLeft)}</div>
      </div>
    </div>
  );
};

export default CircularTimer;
